import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { Preferences } from '@capacitor/preferences';

import { MOBILE_NUMBERS_KEY } from '@/utils/constants';
import { MobileNumber } from '@/utils/types';

interface MobileNumberContextType {
  mobileNumbers: MobileNumber[];
  loading: boolean;
  saveMobileNumber: (label: string, phoneNumber: string) => Promise<void>;
  removeMobileNumber: (label: string) => Promise<void>;
  getMobileNumber: (label: string) => string | undefined;
  getMobileNumbers: () => Promise<void>;
}

const MobileNumberContext = createContext<MobileNumberContextType | undefined>(
  undefined
);

interface MobileNumberProviderProps {
  children: React.ReactNode;
}

export const MobileNumberProvider: React.FC<MobileNumberProviderProps> = ({
  children
}) => {
  const [mobileNumbers, setMobileNumbers] = useState<MobileNumber[]>([]);
  const [loading, setLoading] = useState(false);

  const getMobileNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const { value } = await Preferences.get({
        key: MOBILE_NUMBERS_KEY
      });

      if (value) {
        const parsedNumbers = JSON.parse(value);
        // Sort by label length (longest to shortest)
        const sortedNumbers = parsedNumbers.sort(
          (a: MobileNumber, b: MobileNumber) => {
            const labelA = Object.keys(a)[0];
            const labelB = Object.keys(b)[0];
            return labelB.length - labelA.length;
          }
        );
        setMobileNumbers(sortedNumbers);
      } else {
        setMobileNumbers([]);
      }
    } catch {
      setMobileNumbers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveMobileNumber = useCallback(
    async (label: string, phoneNumber: string) => {
      try {
        const newEntry = { [label]: phoneNumber };
        const existingIndex = mobileNumbers.findIndex(
          (entry) => Object.keys(entry)[0] === label
        );

        let updatedNumbers: MobileNumber[];
        if (existingIndex >= 0) {
          // Update existing entry
          updatedNumbers = [...mobileNumbers];
          updatedNumbers[existingIndex] = newEntry;
        } else {
          // Add new entry
          updatedNumbers = [...mobileNumbers, newEntry];
        }

        // Sort by label length (longest to shortest)
        updatedNumbers.sort((a: MobileNumber, b: MobileNumber) => {
          const labelA = Object.keys(a)[0];
          const labelB = Object.keys(b)[0];
          return labelB.length - labelA.length;
        });

        await Preferences.set({
          key: MOBILE_NUMBERS_KEY,
          value: JSON.stringify(updatedNumbers)
        });

        setMobileNumbers(updatedNumbers);
      } catch {}
    },
    [mobileNumbers]
  );

  const removeMobileNumber = useCallback(
    async (label: string) => {
      try {
        const updatedNumbers = mobileNumbers.filter(
          (entry) => Object.keys(entry)[0] !== label
        );

        await Preferences.set({
          key: MOBILE_NUMBERS_KEY,
          value: JSON.stringify(updatedNumbers)
        });

        setMobileNumbers(updatedNumbers);
      } catch {}
    },
    [mobileNumbers]
  );

  const getMobileNumber = useCallback(
    (label: string) => {
      const entry = mobileNumbers.find(
        (entry) => Object.keys(entry)[0] === label
      );
      return entry ? entry[label] : undefined;
    },
    [mobileNumbers]
  );

  useEffect(() => {
    getMobileNumbers();
  }, [getMobileNumbers]);

  return (
    <MobileNumberContext.Provider
      value={{
        mobileNumbers,
        loading,
        saveMobileNumber,
        removeMobileNumber,
        getMobileNumber,
        getMobileNumbers
      }}
    >
      {children}
    </MobileNumberContext.Provider>
  );
};

export const useMobileNumber = () => {
  const context = useContext(MobileNumberContext);
  if (context === undefined) {
    throw new Error(
      'useMobileNumber must be used within a MobileNumberProvider'
    );
  }
  return context;
};
